import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { PricingStore } from "../../s25.event.pricing.store";
import { S25EditableTextComponent } from "../../../../s25-editable/s25-editable-text/s25.editable.text.component";
import { S25FadingSuccessMsgComponent } from "../../../../s25-fading-success-msg/s25.fading.success.msg.component";
import { S25LoadingInlineStaticComponent } from "../../../../s25-loading/s25.loading.inline.static.component";
import { Bind } from "../../../../../decorators/bind.decorator";

/** One organization's invoice number, as reported on the invoice it is billed on. */
@TypeManagerDecorator("s25-ng-invoice-number")
@Component({
    selector: "s25-ng-invoice-number",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25EditableTextComponent, S25FadingSuccessMsgComponent, S25LoadingInlineStaticComponent],
    template: `
        <span class="invoice-number--label" aria-hidden="true">Invoice #</span>

        <div class="invoice-number--field">
            @if (canEdit()) {
                <s25-ng-editable-text
                    [max]="80"
                    [(val)]="invoiceNumber"
                    [hasCommit]="true"
                    [fieldId]="fieldId()"
                    [ariaLabel]="ariaLabel()"
                    [placeholder]="'None'"
                    [onCommit]="save"
                />
            } @else {
                <span class="invoice-number--value">{{ invoiceNumber() || "None" }}</span>
            }

            @if (saving() || error() || savedMessage()) {
                <span class="invoice-number--status">
                    <!-- The editable closes as soon as Save is clicked, so the spinner shows the wait. -->
                    @if (saving()) {
                        <s25-ng-loading-inline-static [text]="null" />
                    } @else if (error()) {
                        <span class="ngRed" role="alert">{{ error() }}</span>
                    } @else {
                        <s25-ng-fading-success-msg [(message)]="savedMessage" />
                    }
                </span>
            }
        </div>
    `,
    styles: `
        .invoice-number--label {
            font-size: 11px;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--color--text-muted);
            user-select: none;
        }

        .invoice-number--value,
        :host ::ng-deep .s25-editable {
            font-size: 17px;
            font-weight: 600;
        }

        /* xeditable paints .editable-click blue, which cannot reach 4.5:1 on the header strip. The
           pencil and the hover border carry the affordance instead. */
        :host ::ng-deep .c-editable-text {
            color: inherit;
        }

        :host-context(body#s25.nm-party--on) ::ng-deep .c-editable-text {
            color: inherit !important;
        }

        :host ::ng-deep input.c-input {
            max-width: 14rem;
        }

        /* The field and whatever it has to say about the last save, side by side, so a message
           grows into the empty middle of the strip rather than under the table below it. */
        .invoice-number--field {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.5rem;
        }

        /* Ahead of the value, so a message or spinner never nudges the number out of line with the
           figures beside it. */
        .invoice-number--status {
            order: -1;
            font-size: 12px;
            white-space: nowrap;
        }

        :host ::ng-deep .c-loading {
            width: 16px;
            height: 16px;
        }
    `,
})
export class S25InvoiceNumberComponent {
    private readonly pricingStore = inject(PricingStore);

    readonly orgId = input.required<number>();
    readonly orgName = input("");

    protected readonly fieldId = computed(() => `invoice-num-${this.orgId()}`);
    // The visible label is decorative, so the field carries the name, and says which org it numbers.
    protected readonly ariaLabel = computed(() => (this.orgName() ? `Invoice # for ${this.orgName()}` : "Invoice #"));
    protected readonly canEdit = this.pricingStore.canEditInvoiceNumber;

    private readonly storedInvoiceNumber = computed(
        () => this.pricingStore.invoiceNumberByOrg().get(this.orgId()) ?? "",
    );

    readonly invoiceNumber = linkedSignal(() => this.storedInvoiceNumber());

    protected readonly saving = signal(false);
    readonly error = signal("");
    readonly savedMessage = signal("");

    @Bind
    async save(newInvoiceNumber: string) {
        if (this.saving()) return;
        this.saving.set(true);
        this.error.set("");

        const { error } = await this.pricingStore.updateInvoiceNumber(this.orgId(), newInvoiceNumber ?? "");
        this.saving.set(false);

        if (error) {
            this.error.set(error);
            this.invoiceNumber.set(this.storedInvoiceNumber());
            return;
        }

        this.savedMessage.set("Invoice # saved");
    }
}
